import React, { useState, useEffect } from "react";
import logo from "assets/img/logo.png";
import axios from "axios";
import Cookies from "js-cookie";
import { useToast } from "@chakra-ui/react";
import config from "config";
import { Link as RouterLink, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PasswordStrengthBar from "components/PasswordStrength/PasswordStrengthBar";
import { isPasswordStrong, isPasswordTooLong, MIN_PASSWORD_LENGTH, MAX_PASSWORD_LENGTH } from "utils/passwordUtils";
import { translateBackendError } from "utils/errorUtils";
import GoogleSignInButton from "components/GoogleSignInButton";

// Chakra imports
import {
  Box,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  Icon,
  Link,
  Text,
  keyframes,
  useDisclosure,
} from "@chakra-ui/react";
import { FaEye, FaEyeSlash, FaDesktop, FaMobileAlt, FaSync, FaChevronDown, FaShieldAlt } from "react-icons/fa";
import MufynButton from "components/Button";

// Animations
const float = keyframes`
  0%, 100% { transform: translateY(0px) rotate(0deg); }
  50% { transform: translateY(-20px) rotate(3deg); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 0.4; transform: scale(1); }
  50% { opacity: 0.8; transform: scale(1.05); }
`;

const gradientMove = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 20px rgba(34, 197, 94, 0.3), 0 0 40px rgba(34, 197, 94, 0.1); }
  50% { box-shadow: 0 0 40px rgba(34, 197, 94, 0.5), 0 0 80px rgba(34, 197, 94, 0.2); }
`;

const slideUp = keyframes`
  0% { opacity: 0; transform: translateY(30px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

function SignUp() {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const location = useLocation();

  // Support redirect after signup (forwarded from pricing → signin → signup)
  const searchParams = new URLSearchParams(location.search);
  const redirectParam = searchParams.get("redirect");
  const passwordTooShortMessage = t("password.error.too_short", {
    count: MIN_PASSWORD_LENGTH,
    defaultValue: `Password must be at least ${MIN_PASSWORD_LENGTH} characters long`
  });
  const passwordTooLongMessage = t("password.error.too_long", {
    count: MAX_PASSWORD_LENGTH,
    defaultValue: `Password must be at most ${MAX_PASSWORD_LENGTH} characters long`
  });

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingEmail, setIsCheckingEmail] = useState(false);
  const [isSent, setIsSent] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const { isOpen: isDisclaimerOpen, onToggle: onDisclaimerToggle } = useDisclosure();

  // Utiliser la langue du navigateur pour les pages publiques (signup)
  useEffect(() => {
    const getBrowserLanguage = () => {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      return ['en', 'fr'].includes(langCode) ? langCode : 'en';
    };
    
    // Appliquer la langue du navigateur seulement si aucune langue n'est définie
    if (!i18n.language || i18n.language === 'en') {
      i18n.changeLanguage(getBrowserLanguage());
    }
  }, [i18n]);

  useEffect(() => {
    document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "unset";
    };
  }, []);

  const handleEmailNext = async (event) => {
    event.preventDefault();
    if (!email || !email.includes("@")) {
      toast({
        title: t("signup.error.title"),
        description: t("signup.error.fill_fields"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsCheckingEmail(true);
    try {
      await axios.post(`${config.apiUrl}/check-email`, { email });
      setStep(2);
    } catch (error) {
      if (error.response?.status === 409) {
        toast({
          title: t("signup.error.title"),
          description: t("signup.error.email_taken"),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      } else {
        toast({
          title: t("signup.error.title"),
          description: error.response?.data?.message
            ? translateBackendError(error.response.data.message, t, "signup.error.fill_fields")
            : t("signup.error.fill_fields"),
          status: "error",
          duration: 5000,
          isClosable: true,
          position: "top-right",
        });
      }
    } finally {
      setIsCheckingEmail(false);
    }
  };

  const handleRegister = async (event) => {
    event.preventDefault();

    if (!password) {
      toast({
        title: t("signup.error.title"),
        description: t("signup.error.fill_fields"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (!isPasswordStrong(password)) {
      const errorMessage = isPasswordTooLong(password) ? passwordTooLongMessage : passwordTooShortMessage;
      toast({
        title: t("signup.error.title"),
        description: errorMessage,
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    if (password !== confirmPassword) {
      toast({
        title: t("signup.error.title"),
        description: t("signup.error.password_mismatch"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
      return;
    }

    setIsLoading(true);
    try {
      // Get browser language for the registration
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      const language = ['en', 'fr'].includes(langCode) ? langCode : 'en';
      
      const response = await axios.post(`${config.apiUrl}/register`, {
        email,
        password,
        language,
        locale: browserLang,
      });
      
      if (!response.data || !response.data.message) {
        throw new Error("Registration failed - no response");
      }

      // Auto-login: set cookies from token returned by backend
      const { token } = response.data;
      if (token) {
        const tokenExpiration = 7; // days (matches backend default)
        Cookies.set("token", token, {
          expires: tokenExpiration,
          sameSite: "lax",
          secure: !config.isDev,
        });
        Cookies.set("loggedIn", "1", {
          expires: tokenExpiration,
          sameSite: "lax",
          secure: !config.isDev,
        });

        // Clear stored redirect intent
        try {
          sessionStorage.removeItem("mufyn_post_login_redirect");
          localStorage.removeItem("mufyn_post_login_redirect");
        } catch (e) {}

        // Store redirect for after onboarding if needed
        if (redirectParam) {
          try {
            sessionStorage.setItem("mufyn_post_login_redirect", redirectParam);
            localStorage.setItem("mufyn_post_login_redirect", redirectParam);
          } catch (e) {}
        }

        // Redirect to onboarding wizard for new users
        setTimeout(() => {
          window.location.href = "/#/auth/onboarding";
        }, 100);
        return;
      }

      // Fallback: no token in response (should not happen), show sign-in link
      if (redirectParam) {
        try {
          sessionStorage.setItem("mufyn_post_login_redirect", redirectParam);
          localStorage.setItem("mufyn_post_login_redirect", redirectParam);
        } catch (e) {}
      }
    
      setIsSent(true);
    } catch (error) {
      toast({
        title: t("signup.error.title"),
        description: error.response?.data?.message
          ? translateBackendError(error.response.data.message, t, "signup.error.fill_fields")
          : t("signup.error.fill_fields"),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSuccess = async (credential) => {
    setIsGoogleLoading(true);
    try {
      const browserLang = navigator.language || navigator.userLanguage;
      const langCode = browserLang.split('-')[0];
      const language = ['en', 'fr'].includes(langCode) ? langCode : 'en';

      const response = await axios.post(`${config.apiUrl}/google-auth`, {
        credential,
        token_expiration: 7,
        language,
        locale: browserLang,
      });

      const { token, isNewUser } = response.data;
      if (token) {
        const tokenExpiration = 7;
        Cookies.set("token", token, {
          expires: tokenExpiration,
          sameSite: "lax",
          secure: !config.isDev,
        });
        Cookies.set("loggedIn", "1", {
          expires: tokenExpiration,
          sameSite: "lax",
          secure: !config.isDev,
        });
        // Store redirect for after onboarding if needed
        if (redirectParam) {
          try {
            sessionStorage.setItem("mufyn_post_login_redirect", redirectParam);
            localStorage.setItem("mufyn_post_login_redirect", redirectParam);
          } catch (e) {}
        } else {
          try {
            sessionStorage.removeItem("mufyn_post_login_redirect");
            localStorage.removeItem("mufyn_post_login_redirect");
          } catch (e) {}
        }

        // Redirect to onboarding for new users, dashboard for existing
        const target = isNewUser ? "/#/auth/onboarding" : (redirectParam ? `/#${redirectParam}` : "/");
        setTimeout(() => {
          window.location.href = target;
        }, 100);
      }
    } catch (error) {
      toast({
        title: t("signup.error.title"),
        description: error.response?.data?.message
          ? translateBackendError(error.response.data.message, t, "signup.error.fill_fields")
          : t("login.error.google_failed", "Google sign-in failed. Please try again."),
        status: "error",
        duration: 5000,
        isClosable: true,
        position: "top-right",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  // Input styles
  const inputStyles = {
    size: "lg",
    bg: "rgba(15, 20, 30, 0.6)",
    border: "1px solid rgba(255, 255, 255, 0.1)",
    borderRadius: "16px",
    color: "white",
    h: "56px",
    fontSize: "md",
    _placeholder: { color: "gray.500" },
    _hover: {
      border: "1px solid rgba(34, 197, 94, 0.3)",
      bg: "rgba(15, 20, 30, 0.8)",
    },
    _focus: {
      border: "1px solid rgba(34, 197, 94, 0.6)",
      boxShadow: "0 0 0 3px rgba(34, 197, 94, 0.15), inset 0 0 20px rgba(34, 197, 94, 0.05)",
      bg: "rgba(15, 20, 30, 0.8)",
    },
    transition: "all 0.3s ease",
  };

  if (isSent) {
    return (
      <Box minH="100vh" w="100%" position="relative" overflow="hidden" bg="#0a0d14">
        <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
          <Box
            position="absolute"
            top="0"
            left="0"
            right="0"
            bottom="0"
            bgGradient="radial(ellipse at 50% 50%, rgba(34, 197, 94, 0.15) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
          />
        </Box>
        <Flex position="relative" zIndex="1" minH="100vh" align="center" justify="center" direction="column" p={8}>
          <Box mb={0}>
            <img
              src={logo}
              width="120px"
              alt="logo"
              style={{ filter: "drop-shadow(0 0 25px rgba(34, 197, 94, 0.4))" }}
            />
          </Box>
          <Heading
            color="white"
            fontSize="36px"
            fontWeight="bold"
            bgGradient="linear(to-r, #fff, #4ade80, #fff)"
            bgClip="text"
            backgroundSize="200% auto"
            animation={`${gradientMove} 5s linear infinite`}
            mb={4}
          >
            {config.appName}
          </Heading>
          <Text color="gray.400" fontSize="lg" textAlign="center" mb={6}>
            {t("signup.success.description")}
          </Text>
          <Link
            as={RouterLink}
            to={redirectParam ? `/auth/signin?redirect=${encodeURIComponent(redirectParam)}` : "/auth/signin"}
            px={8}
            py={4}
            borderRadius="16px"
            bg="linear-gradient(135deg, #22c55e 0%, #10b981 100%)"
            color="white"
            fontWeight="bold"
            _hover={{
              transform: "translateY(-2px)",
              boxShadow: "0 20px 40px -10px rgba(34, 197, 94, 0.4)",
            }}
            transition="all 0.3s ease"
          >
            {t("signup.signin")}
          </Link>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      minH="100vh"
      w="100%"
      position="relative"
      overflow="hidden"
      bg="#0a0d14"
    >
      {/* Animated Background */}
      <Box position="absolute" top="0" left="0" right="0" bottom="0" zIndex="0">
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bgGradient="radial(ellipse at 30% 20%, rgba(34, 197, 94, 0.15) 0%, transparent 50%), radial(ellipse at 70% 80%, rgba(16, 185, 129, 0.1) 0%, transparent 50%), linear(to-br, #0a0d14, #0f1720, #0a0d14)"
        />
        
        {/* Floating orbs */}
        <Box
          position="absolute"
          top="10%"
          left="5%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(34, 197, 94, 0.2) 0%, transparent 70%)"
          filter="blur(40px)"
          animation={`${float} 8s ease-in-out infinite`}
        />
        <Box
          position="absolute"
          bottom="20%"
          right="10%"
          w="400px"
          h="400px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(74, 222, 128, 0.15) 0%, transparent 70%)"
          filter="blur(60px)"
          animation={`${float} 10s ease-in-out infinite reverse`}
        />
        <Box
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          w="600px"
          h="600px"
          borderRadius="full"
          bg="radial-gradient(circle, rgba(16, 185, 129, 0.08) 0%, transparent 60%)"
          filter="blur(80px)"
          animation={`${pulse} 6s ease-in-out infinite`}
        />
        
        {/* Grid pattern */}
        <Box
          position="absolute"
          top="0"
          left="0"
          right="0"
          bottom="0"
          opacity="0.03"
          backgroundImage={`
            linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)
          `}
          backgroundSize="80px 80px"
        />

        {/* Animated lines */}
        <Box
          position="absolute"
          top="20%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.3), transparent)"
          animation={`${shimmer} 3s linear infinite`}
          backgroundSize="200% 100%"
        />
        <Box
          position="absolute"
          top="80%"
          left="0"
          right="0"
          h="1px"
          background="linear-gradient(90deg, transparent, rgba(74, 222, 128, 0.2), transparent)"
          animation={`${shimmer} 4s linear infinite 1s`}
          backgroundSize="200% 100%"
        />
      </Box>

      {/* Main Content */}
      <Flex
        position="relative"
        zIndex="1"
        minH="100vh"
        align="center"
        justify="center"
        p={{ base: 4, md: 8 }}
        py={{ base: 8, md: 12 }}
      >
        <Box
          as="form"
          onSubmit={step === 1 ? handleEmailNext : handleRegister}
          w="100%"
          maxW="480px"
          animation={`${slideUp} 0.8s ease-out`}
        >
          {/* Glass Card */}
          <Box
            position="relative"
            p={{ base: 6, md: 10 }}
            borderRadius="24px"
            bg="linear-gradient(135deg, rgba(15, 20, 30, 0.8) 0%, rgba(20, 30, 40, 0.6) 100%)"
            backdropFilter="blur(20px)"
            border="1px solid rgba(34, 197, 94, 0.15)"
            boxShadow="0 25px 50px -12px rgba(0, 0, 0, 0.5), inset 0 1px 0 rgba(255, 255, 255, 0.05)"
            overflow="hidden"
            _before={{
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              h: "1px",
              background: "linear-gradient(90deg, transparent, rgba(34, 197, 94, 0.5), transparent)",
            }}
          >
            {/* Logo & Header */}
            <Flex direction="column" align="center" mb={6}>
              <Box mb={0}>
                <img src={logo} width="120px" alt="logo" style={{ filter: "drop-shadow(0 0 25px rgba(34, 197, 94, 0.4))" }} />
              </Box>
              <Heading
                color="white"
                fontSize={{ base: "24px", md: "32px" }}
                fontWeight="bold"
                bgGradient="linear(to-r, #fff, #4ade80, #fff)"
                bgClip="text"
                backgroundSize="200% auto"
                animation={`${gradientMove} 5s linear infinite`}
              >
                {config.appName}
              </Heading>
              <Text mt={2} color="gray.400" fontSize="sm" textAlign="center">
                {step === 1 ? t("signup.description") : t("signup.step2_description")}
              </Text>
            </Flex>

            {/* Step indicator */}
            <Flex justify="center" mb={6} gap={2} align="center">
              <Box
                w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center"
                bg={step >= 1 ? "linear-gradient(135deg, #22c55e 0%, #10b981 100%)" : "rgba(255,255,255,0.08)"}
                color="white" fontSize="sm" fontWeight="bold" transition="all 0.3s ease"
              >
                {step > 1 ? "✓" : "1"}
              </Box>
              <Box w="40px" h="2px" bg={step >= 2 ? "#22c55e" : "rgba(255,255,255,0.1)"} borderRadius="full" transition="all 0.3s ease" />
              <Box
                w="32px" h="32px" borderRadius="full" display="flex" alignItems="center" justifyContent="center"
                bg={step >= 2 ? "linear-gradient(135deg, #22c55e 0%, #10b981 100%)" : "rgba(255,255,255,0.08)"}
                color={step >= 2 ? "white" : "gray.500"} fontSize="sm" fontWeight="bold" transition="all 0.3s ease"
              >
                2
              </Box>
            </Flex>

            {/* Form Fields */}
            <Box>
              {step === 1 ? (
                <>
                  {/* Why create an account? - Expandable disclaimer */}
                  <Box mb={4}>
                    <Flex
                      justify="center"
                      align="center"
                      gap={1}
                      cursor="pointer"
                      onClick={onDisclaimerToggle}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onDisclaimerToggle(); }}
                    >
                      <Icon as={FaShieldAlt} color="gray.600" boxSize="10px" />
                      <Text color="gray.600" fontSize="xs" fontWeight="500" _hover={{ color: "gray.400" }} transition="color 0.2s">
                        {t("signup.disclaimer.toggle")}
                      </Text>
                      <Icon
                        as={FaChevronDown}
                        color="gray.600"
                        boxSize="10px"
                        transform={isDisclaimerOpen ? "rotate(180deg)" : "rotate(0deg)"}
                        transition="transform 0.2s ease"
                      />
                    </Flex>
                    <Collapse in={isDisclaimerOpen} animateOpacity>
                      <Box
                        mt={2}
                        mx="auto"
                        maxW="400px"
                        px={4}
                        py={3}
                        borderRadius="12px"
                        bg="rgba(34, 197, 94, 0.04)"
                        border="1px solid rgba(34, 197, 94, 0.08)"
                      >
                        <Text color="gray.500" fontSize="xs" lineHeight="1.6">
                          {t("signup.disclaimer.text")}
                        </Text>
                        <Link
                          href="https://nelx.net/privacy"
                          isExternal
                          color="#4ade80"
                          fontSize="xs"
                          fontWeight="500"
                          _hover={{ color: "#22c55e", textDecoration: "none" }}
                          transition="color 0.2s"
                          mt={1}
                          display="inline-block"
                        >
                          {t("signup.disclaimer.privacy_link")} →
                        </Link>
                      </Box>
                    </Collapse>
                  </Box>

                  {/* Step 1: Email */}
                  <FormControl mb={6}>
                    <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                      {t("signup.email")}
                    </FormLabel>
                    <Input
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      type="email"
                      autoComplete="email"
                      placeholder={t("signup.email")}
                      {...inputStyles}
                    />
                  </FormControl>

                  <MufynButton
                    onClick={handleEmailNext}
                    size="xl"
                    variant="primary"
                    fullWidth
                    isLoading={isCheckingEmail}
                    loadingText={t("signup.loading")}
                    withRipple
                  >
                    {t("signup.next")}
                  </MufynButton>

                  {/* Divider */}
                  <Flex align="center" my={6}>
                    <Box flex="1" h="1px" bg="rgba(255, 255, 255, 0.1)" />
                    <Text px={4} color="gray.500" fontSize="sm">
                      {t("login.or", "or")}
                    </Text>
                    <Box flex="1" h="1px" bg="rgba(255, 255, 255, 0.1)" />
                  </Flex>

                  {/* Google Sign-Up */}
                  <GoogleSignInButton
                    onSuccess={handleGoogleSuccess}
                    isLoading={isGoogleLoading}
                  />
                </>
              ) : (
                <>
                  {/* Step 2: Password */}
                  <Box mb={4} p={3} borderRadius="12px" bg="rgba(34, 197, 94, 0.08)" border="1px solid rgba(34, 197, 94, 0.15)">
                    <Text fontSize="sm" color="gray.400">
                      {email}
                    </Text>
                  </Box>

                  <FormControl mb={4}>
                    <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                      {t("signup.password")}
                    </FormLabel>
                    <InputGroup>
                      <Input
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        type={showPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t("signup.password")}
                        {...inputStyles}
                      />
                      <InputRightElement h="56px">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={showPassword ? <FaEyeSlash /> : <FaEye />}
                          onClick={() => setShowPassword(!showPassword)}
                          color="gray.500"
                          _hover={{ color: "gray.300" }}
                          aria-label={showPassword ? t("common.hide_password", "Hide password") : t("common.show_password", "Show password")}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <FormControl mb={2}>
                    <FormLabel color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                      {t("signup.confirm_password")}
                    </FormLabel>
                    <InputGroup>
                      <Input
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        type={showConfirmPassword ? "text" : "password"}
                        autoComplete="new-password"
                        placeholder={t("signup.confirm_password")}
                        {...inputStyles}
                      />
                      <InputRightElement h="56px">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          icon={showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          color="gray.500"
                          _hover={{ color: "gray.300" }}
                          aria-label={showConfirmPassword ? t("common.hide_password", "Hide password") : t("common.show_password", "Show password")}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>
                  <Box mt={3} mb={6}>
                    <PasswordStrengthBar password={password} />
                  </Box>

                  {/* Submit Button */}
                  <MufynButton
                    type="submit"
                    size="xl"
                    variant="primary"
                    fullWidth
                    isLoading={isLoading}
                    loadingText={t("signup.registering", "Inscription...")}
                    withRipple
                  >
                    {t("signup.button")}
                  </MufynButton>

                  {/* Back button */}
                  <Flex justify="center" mt={3}>
                    <Text
                      color="gray.500" fontSize="sm" cursor="pointer"
                      _hover={{ color: "#4ade80" }} transition="color 0.2s"
                      onClick={() => setStep(1)}
                    >
                      ← {t("signup.back")}
                    </Text>
                  </Flex>
                </>
              )}
            </Box>

            {/* Footer Link */}
            <Flex justify="center" mt={6}>
              <Text color="gray.500" fontSize="sm">
                {t("signup.already_have_account")}{" "}
                <Link
                  as={RouterLink}
                  to={redirectParam ? `/auth/signin?redirect=${encodeURIComponent(redirectParam)}` : "/auth/signin"}
                  color="#4ade80"
                  fontWeight="semibold"
                  _hover={{
                    color: "#22c55e",
                    textDecoration: "none",
                  }}
                  transition="color 0.2s ease"
                >
                  {t("signup.signin")}
                </Link>
              </Text>
            </Flex>

            {/* Sync hint — clickable, opens the Play Store (or website in-app) */}
            <Flex justify="center" mt={4}>
              <Link
                href={window.AndroidBridge ? "https://mufyn.ch" : config.playStoreUrl}
                isExternal
                display="flex"
                alignItems="center"
                gap={1.5}
                px={3}
                py={1.5}
                borderRadius="full"
                bg="rgba(34, 197, 94, 0.04)"
                border="1px solid rgba(34, 197, 94, 0.08)"
                transition="all 0.2s ease"
                _hover={{
                  textDecoration: "none",
                  bg: "rgba(34, 197, 94, 0.08)",
                  borderColor: "rgba(34, 197, 94, 0.25)",
                }}
              >
                <Icon as={FaMobileAlt} color="gray.600" boxSize="10px" />
                <Icon as={FaSync} color="gray.600" boxSize="8px" />
                <Icon as={FaDesktop} color="gray.600" boxSize="10px" />
                <Text color="gray.500" fontSize="xs" fontWeight="400" ml={0.5}>
                  {t(window.AndroidBridge ? "welcome.sync_hint" : "welcome.sync_hint_desktop")}
                </Text>
              </Link>
            </Flex>

          </Box>
        </Box>
      </Flex>
    </Box>
  );
}

export default SignUp;
